import React, { useEffect, useRef, useState } from 'react'
import SupportListWrapper from 'core/page/SupportListWrapper'
import { useTranslation } from 'react-i18next'
import {
  customerPendingLog,
  deletePendingEmail,
  fetchEmailTemplates,
  fetchReceiverEmail,
  resendAllPendingEmail,
  resendPendingEmail,
  resetFilters,
  updateAppId,
  updateModuleID
} from '../state/actions'
import { CustomerEmailInterface } from '@cbgms/api/modules/customers'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useSearchCustomers } from '../hooks/use-search-pending-log'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { Dropdown, LoaderWithBackground } from '@carsys/ui'
import { Dispatch } from 'redux'
import { RefreshDataFunc } from '@cbgms/components/grid/Knock/KnockDataLoader'
import { useNotifier } from '@cbgms/base/notifier'
import store from '../state/store'
import { getInitialConcepts } from './helpers/constants'
import SendIcon from '@mui/icons-material/Send'
import { v4 as uuidv4 } from 'uuid'
import { makeStyles } from '@mui/styles'
import { IRootReducerState } from 'state/reducers'

const useStyles = makeStyles(() => ({
  title: {
    marginBottom: 10,
    fontSize: 20,
    fontWeight: 700
  },
  gridContainer: {
    width: '120%'
  },
  stackContainer: {
    width: '100%',
    marginTop: 16,
    marginBottom: 16,
    marginLeft: 16
  },
  dropdown: {
    minWidth: 150,
    marginX: 1
  },
  button: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  },
  dialogContentText: {
    marginTop: 16,
    maxHeight: 200,
    overflowY: 'auto'
  },
  textField: {
    marginTop: 16
  },
  noButton: {
    color: 'white'
  }
}))

export const CustomDataGrid: React.FC = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch<Dispatch>()
  const { getSearchComponent, apiSearchQuery, updateListBYDropDownFilters } = useSearchCustomers()
  const [isLoading, setIsLoading] = useState(false)
  const [conceptsList, setConceptsList] = useState([{ key: 'none', value: 'None', text: t('global.nofilters') }])
  const refreshData = useRef<RefreshDataFunc | undefined>(undefined)
  const notifier = useNotifier()
  const CONCEPTS = getInitialConcepts(t)
  const [seletedConceptForFilter, setSelectedConceptForFilter] = React.useState<string | undefined>(CONCEPTS[0].value)
  const [seed, setSeed] = useState<string>(uuidv4())
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalAction, setModalAction] = useState<'sendAll' | 'delete' | 'resend' | 'error'>()
  const [pendingDeleteData, setPendingDeleteData] = useState<{ PendingLogsOutput: CustomerEmailInterface } | null>(null)
  const [email, setEmail] = useState('')
  const [pendingEmailUUID, setPendingEmailUUID] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const App_Id = useSelector((state: IRootReducerState) => state.auth.user.AppId)
  const fetchEmailAttachementList = async (appId: string) => {
    try {
      const emailsTemplatesData = await dispatch(fetchEmailTemplates(appId))
      const templateNames = emailsTemplatesData.Data.map((template: { EmailTemplateUUID: string; TemplateName: string }) => ({
        key: template.EmailTemplateUUID,
        value: template.TemplateName,
        text: template.TemplateName
      }))

      setConceptsList([{ key: 'none', value: 'None', text: t('global.nofilters') }, ...templateNames])
      setIsLoading(false)
    } catch (error) {
      notifier.error(String(error))
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (App_Id) {
      // keep the local pending-email store's AppId in sync with the logged-in user, otherwise search hits the wrong app
      store.dispatch(updateAppId(Number(App_Id)))
      fetchEmailAttachementList(String(App_Id))
      if (refreshData.current) {
        refreshData.current()
      }
    }
  }, [App_Id])

  const onDataLoaderInitialized = React.useCallback(
    (refreshDataFunction: RefreshDataFunc) => {
      refreshData.current = refreshDataFunction
      if (App_Id) {
        refreshDataFunction()
      }
    },
    [App_Id]
  )

  const handleConceptsFilterChange = (value: string) => {
    setSelectedConceptForFilter(value)
    store.dispatch(updateModuleID(value))
  }

  const refreshLoadData = async () => {
    setSelectedConceptForFilter(CONCEPTS[0].value)
    store.dispatch(resetFilters())
    // reset apiSearchQuery to '' before reloading, otherwise the grid refetches with the stale (previously applied) filter
    await updateListBYDropDownFilters()
    if (refreshData.current) {
      refreshData.current()
    }
  }

  const handleApplyFilters = () => {
    updateListBYDropDownFilters()
  }

  const onPendingLog = async (_e: React.SyntheticEvent, data: { PendingLogsOutput: CustomerEmailInterface }) => {
    const { ReceiverEmail, PendingEmailUUID } = data.PendingLogsOutput
    if (ReceiverEmail) {
      setEmail(ReceiverEmail)
    } else {
      const response = await dispatch(fetchReceiverEmail(PendingEmailUUID))
      if (response && response.Data && response.Data.length > 0) {
        const receiverEmail = response.Data[0].ReceiverEmail
        setEmail(receiverEmail)
      }
    }
    setPendingEmailUUID(PendingEmailUUID)
    setModalAction('resend')
    setIsModalOpen(true)
  }

  const onDoubleClickFunction = (data: { PendingLogsOutput: CustomerEmailInterface }) => {
    setErrorMessage(data.PendingLogsOutput.ErrorMessage)
    setModalAction('error')
    setIsModalOpen(true)
  }

  const handleSendButtonClick = () => {
    setModalAction('sendAll')
    setIsModalOpen(true)
  }

  const onPendingDelete = (_e: React.SyntheticEvent, data: { PendingLogsOutput: CustomerEmailInterface }) => {
    setPendingDeleteData(data)
    setModalAction('delete')
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setPendingDeleteData(null)
    setPendingEmailUUID(null)
    setErrorMessage(null)
  }

  const handleModalConfirm = async () => {
    setIsModalOpen(false)
    if (modalAction === 'sendAll') {
      try {
        const response = await dispatch(resendAllPendingEmail(App_Id as string))
        if (response.Data.Status === 'Success') {
          notifier.success(response.Data.Message)
          setSeed(uuidv4())
        } else {
          notifier.success(t('Pending_Email.send_all_not_success'))
        }
      } catch (error) {
        notifier.error(t('Pending_Error.fetching_status'), error)
      }
    } else if (modalAction === 'delete' && pendingDeleteData) {
      try {
        const response = await dispatch(deletePendingEmail(pendingDeleteData.PendingLogsOutput.PendingEmailUUID))
        if (response.Data.Status === 'Success') {
          notifier.success(t('Pending_Email.deleted_successfully'))
          setSeed(uuidv4())
        } else {
          notifier.success(t('Pending_Email.failed_to_delete'))
        }
      } catch (error) {
        notifier.error(t('Pending_Error.fetching_status'), error)
      }
    } else if (modalAction === 'resend' && pendingEmailUUID && email) {
      try {
        const response = await dispatch(
          resendPendingEmail({
            AppID: App_Id,
            PendingEmailUUID: pendingEmailUUID,
            Email: email
          })
        )

        if (response.Data.Status === 'Success') {
          notifier.success(t('Pending_Email.sent_successfully'))
          setSeed(uuidv4())
        } else {
          notifier.error(t('Pending_Email.failed_to_resend'))
          setSeed(uuidv4())
        }
      } catch (error) {
        notifier.error(t('Pending_Error.fetching_status'), error)
      }
    }
  }

  const getDialogContentText = () => {
    switch (modalAction) {
      case 'sendAll':
        return t('global.are_you_sure_send_all')
      case 'delete':
        return t('global.are_you_sure_delete')
      case 'error':
        return t('global.error_message')
      default:
        return t('global.confirmation_message')
    }
  }

  return (
    <div data-testid='customers-table'>
      <Typography variant='h6' className={classes.title}>
        {t('Customers.PendingEmailLog')}
      </Typography>
      <LoaderWithBackground isLoading={isLoading} data-testid='api-loader' />
      <SupportListWrapper
        key={seed}
        scope='pendingLogsOutput'
        type='pending-email-log'
        onPendingLog={onPendingLog}
        onPendingDelete={onPendingDelete}
        loadAction={params => customerPendingLog({ ...params, appid: App_Id })}
        onDoubleClick={onDoubleClickFunction}
        middleMenuItems={getSearchComponent()}
        translationKey='PendingLogsOutput'
        predefinedSearch={apiSearchQuery}
        hideExport
        tabItems={
          <Grid className={classes.gridContainer}>
            <Stack direction='row' className={classes.stackContainer} spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Dropdown
                  className={classes.dropdown}
                  placeholder={t('global.filter_by_All_Email_Template_Types')}
                  data-testid='dropdown-all-email-template-types'
                  label={t('global.filter_by_All_Email_Template_Types')}
                  onChange={e => handleConceptsFilterChange(e.target.value)}
                  options={conceptsList}
                  value={seletedConceptForFilter}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Button
                  variant='contained'
                  color='primary'
                  className={`ui tiny ml-half ${classes.button}`}
                  onClick={handleApplyFilters}
                  data-testid='apply-filters-button'
                >
                  {t('global.apply_filters')}
                </Button>
                <Link to={window.location.pathname} onClick={refreshLoadData}>
                  <Button variant='outlined' color='inherit' className={`ui tiny ml-half ${classes.button}`} data-testid='reset-button'>
                    {t('global.reset')}
                  </Button>
                </Link>
                <Button
                  variant='contained'
                  color='primary'
                  className={`ui tiny ml-half ${classes.button}`}
                  startIcon={<SendIcon />}
                  onClick={handleSendButtonClick}
                  data-testid='send-all-button'
                >
                  {t('global.send_all')}
                </Button>
              </Grid>
            </Stack>
          </Grid>
        }
        noDataPlaceholder={t('Customers.NoPendingEmailList')}
        onDataLoaderInitialized={onDataLoaderInitialized}
      />
      <Dialog
        open={isModalOpen}
        onClose={handleModalClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        data-testid='confirmation-dialog'
      >
        <DialogTitle id='alert-dialog-title' data-testid='dialog-title'>
          {modalAction === 'error' ? t('global.error') : t('global.confirmation')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description' data-testid='dialog-content-text'>
            {getDialogContentText()}
          </DialogContentText>
          {modalAction === 'resend' && (
            <>
              <DialogContentText mt={2} id='alert-dialog-description'>
                {t('global.enter_email')}
              </DialogContentText>
              <TextField
                label='Email'
                value={email}
                onChange={e => setEmail(e.target.value)}
                fullWidth
                className={classes.textField}
                data-testid='email-input'
              />
            </>
          )}
          {modalAction === 'error' && (
            <DialogContentText id='alert-dialog-error-message' className={classes.dialogContentText} data-testid='error-message'>
              {errorMessage}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {modalAction === 'error' ? (
            <Button onClick={handleModalClose} color='primary' autoFocus variant='contained' data-testid='close-button'>
              {t('global.close')}
            </Button>
          ) : (
            <>
              <Button onClick={handleModalClose} className={classes.noButton} color='secondary' variant='contained' data-testid='no-button'>
                {t('global.no')}
              </Button>
              <Button onClick={handleModalConfirm} color='primary' autoFocus variant='contained' data-testid='yes-button'>
                {t('global.yes')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default CustomDataGrid
